/* eslint @stylistic/max-len: ["error", { "code": 150 }] -- standard 80/100 leaves a lot of unused space */
import React, {
  useState, useEffect, useRef, useMemo,
} from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import {
  Alert, Label, ProgressCircle, Select, SelectOption,
} from '@americanexpress/dls-react';
import { useFetchye } from 'fetchye';
import PropTypes from 'prop-types';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { compose } from 'redux';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { connect, useSelector } from 'react-redux';

/* eslint complexity: ["error", 15] -- Preserve the existing component structure while enforcing its complexity limit. */
function DocRepositories(props) {
  const {
    repositoryView,
    onResponseReset,
    submitResponse,
    icaasMarket,
    icaasFlowType,
    handleRepoChange,
    handleDocTypeRes,
    languageData,
    scrollIntoSelect,
    locale,
    guid,
    repoConfigId,
    useECM,
  } = props;
  const [repoId, setRepoId] = useState('');
  const [selectedDocType, setSelectedDocType] = useState('');
  const [docTypeAvailable, setDocTypeAvailable] = useState(false);

  const selectedItem = useRef(null);
  const prevScrollIntoSelect = useRef();

  // replace inbound url with gateway url
  const urlSet = useECM ? 'eduGatewayUrl' : 'imageViewerDocUploadApiUrl';
  const inboundURL = useSelector((state) => state.getIn(['config', urlSet]));
  const apiPath = useECM ? '/servicing/ecm/docrepositories/' : '/servicing/v1/communications/docrepositories/';

  const requestUrl = useMemo(() => {
    if (!inboundURL || !repoConfigId) return null;
    return `${inboundURL}${apiPath}${repoConfigId}`;
  }, [inboundURL, apiPath, repoConfigId]);

  const reqHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    'icaas-guid': `${guid}`,
    'icaas-market': `${icaasMarket}`,
    'icaas-flow-type': `${icaasFlowType}`,
    'icaas-correlation-id': crypto.randomUUID(),
  }), [guid, icaasMarket, icaasFlowType]);

  const {
    data: repoData,
    error: repoDataError,
    isLoading: repoIsLoading,
  } = useFetchye(
    requestUrl,
    {
      credentials: 'include',
      method: 'GET',
      headers: reqHeaders,
    }
  );

  useEffect(() => {
    prevScrollIntoSelect.current = scrollIntoSelect;
  }, [scrollIntoSelect]);

  useEffect(() => {
    if (selectedDocType === '') {
      /* istanbul ignore next */
      if (prevScrollIntoSelect.current !== scrollIntoSelect && scrollIntoSelect !== null) {
        selectedItem.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedDocType, scrollIntoSelect]);

  useEffect(() => {
    if (submitResponse !== null && submitResponse.status.statusCode === '60200') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- code complexity to refactor by uplifting state not worth it in current setup
      setSelectedDocType('');
    }
  }, [submitResponse]);

  const hasError = () => repoDataError !== undefined || (repoData && repoData.status !== 200);
  if (repoIsLoading) {
    return <div data-testid="repoIsLoading" className="flex flex-align-center flex-justify-center pad-2-tb"><ProgressCircle size="lg" /></div>;
  }

  if (hasError()) {
    return (
      <div data-testid="repoDataError" className="flex flex-align-center flex-justify-center pad-2-tb">
        Error when loading page, please refresh.
      </div>
    );
  }

  const getRepoOptions = () => {
    // check for empty response
    if (!repoData || !repoData.body) return [];
    const repoMap = repoData.body.map((obj) => (
      <SelectOption aria-label={obj.repoNm} key={obj.key} value={obj.id}>{obj.repoNm}</SelectOption>
    ));
    return repoMap;
  };

  const getRepository = (key, flowType) => {
    if (!repoData || !repoData.body) return undefined;
    return repoData.body.find((items) => items.key === key && items.flowType === flowType) || repoData.body.find((items) => items.key === key);
  };

  const getDocTypeOptions = (repoIdKey) => {
    if (!repoIdKey) return null;

    const matchedRepo = getRepository(repoIdKey, icaasFlowType);

    if (!matchedRepo || !matchedRepo.docTypes) return null;

    return matchedRepo.docTypes.map((docType) => (
      <SelectOption
        aria-label={docType.label}
        key={`option-repo-${docType.label}`}
        value={docType.key}
      >
        { docType.label }
      </SelectOption>
    ));
  };

  const onRepoChange = (event) => {
    if (onResponseReset) {
      onResponseReset();
    }
    setRepoId(event.target.value);
    setDocTypeAvailable(event.target.value !== null);
  };

  const onDocTypeChange = (event) => {
    if (onResponseReset) {
      onResponseReset();
    }
    if (handleDocTypeRes) {
      const selectedRepo = getRepository(repoId, icaasFlowType);
      if (handleRepoChange) {
        handleRepoChange(selectedRepo);
      }
      handleDocTypeRes(event.target.value);
    }
    setSelectedDocType(event.target.value);
  };

  switch (repositoryView) {
    case 'onlyDocument':
      return (
        <IntlProvider locale={locale} messages={languageData}>
          <div data-testid="onlyDocumentView" role="navigation">
            <Label id="docTypeLabel" htmlFor="docTypeSelect">
              <FormattedMessage id="fieldLabel" defaultMessage="Document Category" />
            </Label>
            <Select
              role="combobox"
              id="docTypeSelect"
              aria-labelledby="docTypeLabel"
              aria-expanded="false"
              aria-controls="account-options"
              onChange={onDocTypeChange}
              value={selectedDocType}
              fluid={true}
              ref={selectedItem}
              data-testid="repoSelectOnly"
              status={selectedDocType === '' && scrollIntoSelect !== null ? 'error' : 'default'}
            >
              <SelectOption aria-label={languageData.selectOne} key="first-value" id="account-options" value="">
                {languageData.selectOne}
              </SelectOption>
              {getDocTypeOptions(icaasMarket)}
            </Select>
          </div>
          {selectedDocType === '' && scrollIntoSelect !== null && (
          <div className="pad-1-tb" aria-live="assertive">
            <Alert data-testid="selectCategoryAlert">{languageData.selectCategory}</Alert>
          </div>
          )}
        </IntlProvider>
      );
    case 'allHorizontal':
    default:
      return (
        <IntlProvider locale={locale} messages={languageData}>
          <div data-testid="allHorizontalView" className="flex flex-row-md-up flex-column-sm-down" role="navigation">
            <div className="flex-item-grow">
              <Label htmlFor="repoSelect">{languageData.repository || 'Repository'}</Label>
              <Select
                id="repoSelect"
                onChange={onRepoChange}
                data-testid="repoSelect"
                value={repoId}
              >
                <SelectOption aria-label={languageData.selectOne} key="first-value" value="">{languageData.selectOne}</SelectOption>
                {getRepoOptions()}
              </Select>
            </div>
            {docTypeAvailable && (
            <div className="flex-item-grow">
              <Label htmlFor="docTypeSelect" aria-label="Document Type">{languageData.docType}</Label>
              <Select
                id="docTypeSelect"
                data-testid="docTypeSelect"
                onChange={onDocTypeChange}
                value={selectedDocType}
              >
                <SelectOption key="first-value" aria-label={languageData.selectOne} value="">{languageData.selectOne}</SelectOption>
                {getDocTypeOptions(repoId)}
              </Select>
            </div>
            )}
          </div>
        </IntlProvider>
      );
  }
}

DocRepositories.defaultProps = {
  repositoryView: 'allHorizontal',
  onResponseReset: null,
  submitResponse: null,
  icaasMarket: null,
  icaasFlowType: null,
  handleRepoChange: null,
  handleDocTypeRes: null,
  languageData: {},
  scrollIntoSelect: null,
  locale: 'en-US',
  guid: null,
  repoConfigId: null,
  useECM: false,
};

DocRepositories.propTypes = {
  repositoryView: PropTypes.string,
  onResponseReset: PropTypes.func,
  handleRepoChange: PropTypes.func,
  handleDocTypeRes: PropTypes.func,
  submitResponse: PropTypes.instanceOf(Object),
  icaasMarket: PropTypes.string,
  icaasFlowType: PropTypes.string,
  languageData: PropTypes.instanceOf(Object),
  scrollIntoSelect: PropTypes.string,
  locale: PropTypes.string,
  guid: PropTypes.string,
  repoConfigId: PropTypes.string,
  useECM: PropTypes.bool,
};

export function mapStateToProps(state, ownProps) {
  const guid = ownProps.guid || '';
  const icaasMarket = ownProps.icaasMarket || '';
  const repoConfigId = ownProps.repoConfigId || '';
  const icaasFlowType = ownProps.icaasFlowType || '';
  return {
    guid,
    icaasMarket,
    repoConfigId,
    icaasFlowType,
  };
}

export const loadModuleData = async ({ store: { dispatch } }) => {
  await dispatch(loadLanguagePack('axp-doc-repositories', { fallbackLocale: 'en-US' }));
};

DocRepositories.holocron = {
  name: 'axp-doc-repositories',
  loadModuleData,
};

const UnwrappedDocRepositories = DocRepositories;
export { UnwrappedDocRepositories };

export default compose(
  oneAppModuleWrapper('axp-doc-repositories'),
  connect(mapStateToProps)
)(DocRepositories);
